import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { latestListContainerWidget } from "config/resources/sharedFieldsTyped";
import { genericOverTimeChartDto } from "config/utils.jsx";
import { executionFilterPerProjectLink } from "containers/ExecutionFilter/utils";
import { executionsListSubscription } from "containers/Executions/Executions.type";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    exportActionSignature,
    exportFullActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import { bulkCommitActionSignature } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import {
    branchNameColumn,
    gitEnabledColumn,
    gitUrlColumn,
    repositoryNameColumn,
    resourcesNumberColumn,
} from "containers/Search/components/SearchTabs/columns";
import { executionStatusPerTimeQuery } from "containers/Stats/Stats.type";
import resource from "./resource";

export interface ProjectConfig extends ResourceConfig {
    defaultProjectName: string;
}

const resourceName = "project";

const config: ProjectConfig = {
    defaultProjectName: "Default project",
    label: "Project",
    icon: IconCollection.folderTableOutline,
    resourceName,
    resourceClass: "resource",
    layout: [
        {
            partial: "pageHeaderForm",
            config: {
                editIcon: true,
            },
        },
        { partial: "form" },
        { partial: "dashboard" },
        {
            partial: "SearchTabs",
            name: "resourceList",
            filter: (id: string) => ({
                and: [
                    {
                        field: "project_id",
                        op: "eq",
                        value: id,
                    },
                ],
            }),
            showLocationColumn: false,
        },
    ],
    actions: {
        shown: [bulkCommitActionSignature],
        hidden: [
            exportActionSignature,
            exportFullActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
        hidePin: true,
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        gitEnabledColumn(),
        branchNameColumn(setNewValue),
        gitUrlColumn(setNewValue),
        repositoryNameColumn(setNewValue),
        resourcesNumberColumn(),
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            icon
            branch_name
            git_url
            default_track_in_git
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            branch_name
            git_url
            repository_name
            resource_list_project_id (limit: 0, offset: 0, order: "+name") {
                resource_count
            }
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)},
            $icon: String,
            $branch_name: String
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            icon: $icon,
            branch_name: $branch_name
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)},
            $icon: String,
            $branch_name: String
            $repository_name: String
            $default_track_in_git: Boolean
        `,
        updateQuery: `,
            ${resource.graphql.updateQuery(resourceName)}
            icon: $icon,
            branch_name: $branch_name
            repository_name: $repository_name
            default_track_in_git: $default_track_in_git
        `,
    },
    dashboardQueries: {
        executionStatusPerTime: (recordId) => ({
            query: executionStatusPerTimeQuery,
            variables: {
                interval: 7,
                per: "day",
                project_id: recordId,
                allow_normal: true,
                allow_deleted: false,
            },
        }),
    },
    dashboardSubscriptions: {
        latestExecutions: (recordId) => ({
            subscription: executionsListSubscription,
            variables: {
                limit: 3,
                order: "-created_at",
                filter: JSON.stringify({
                    field: "project_id",
                    op: "eq",
                    value: recordId,
                }),
            },
        }),
    },
    form: [],
    dashboard: [
        {
            uid: "project-dashboard-executions",
            title: "Usage & activity",
            component: "CollapsibleSection",
            className: "w-full",
            defaultExpanded: true,
            items: [
                {
                    uid: "widget-1",
                    title: "Daily executions",
                    className: "w-2/3",
                    component: "OverTimeChart",
                    link: executionFilterPerProjectLink,
                    dataTestId: "dailyExecutions",
                    dto: ({ stats }: any) =>
                        stats?.executionStatusPerTime
                            ? genericOverTimeChartDto(stats?.executionStatusPerTime)
                            : null,
                },
                latestListContainerWidget("widget-2", executionFilterPerProjectLink),
            ],
        },
    ],
};
export default config;
